import type {
  BoundingBoxParams,
  CompanyId,
  CubeId,
  CubeLineForTab,
  FieldId,
  HttpRequestLog,
  LogMessage,
  MapInfo,
  ProjectType,
  ProjectWellVsp,
  UpdateCubeLineForTab,
  UploadId,
  UploadMapType,
} from '@core/core/types'
import { throttleCallback } from '@library/utils'
import type { HorizonId, Nullable, ProjectId } from 'common/types'

import { constructClientDispenser } from '../clientDispenserFactory'
import { axiosInstance } from '../common'
import { AuthenticationClient } from './__generated__/Authentication'
import { CubeClient } from './__generated__/Cube'
import type { UserInfo as ApiUserInfo } from './__generated__/data-contracts'
import { FrontendLoggingClient } from './__generated__/FrontendLogging'
import { GeologyClient } from './__generated__/Geology'
import { HorizonClient } from './__generated__/Horizon'
import { HttpClient } from './__generated__/http-client'
import { LayersUploadClient } from './__generated__/LayersUpload'
import { ProjectClient } from './__generated__/Project'
import { ProjectCubeLineClient } from './__generated__/ProjectCubeLine'
import { ProjectSeismicClient } from './__generated__/ProjectSeismic'
import type { BoundingBoxGeometry } from './BffHelpers'
import { convertToGeometry, getUserDeviceInfo } from './BffHelpers'
import {
  convertHttpRequestLogs,
  convertLogMessages,
  convertToApiCubeLineValue,
  convertToApiUpdateCubeLineValue,
  convertToCubeLineValue,
  convertToMapInfo,
  convertWellsVsp,
} from './converters'

const Clients = {
  LayersUploadClient,
  ProjectClient,
  CubeClient,
  ProjectSeismicClient,
  HorizonClient,
  AuthenticationClient,
  GeologyClient,
  FrontendLoggingClient,
  ProjectCubeLineClient,
} as const

const getClient = constructClientDispenser(Clients, HttpClient, axiosInstance)

/// project

export const createProjectApi = (
  name: string,
  type: ProjectType,
  projectYear: number,
  companyId?: Nullable<CompanyId>,
  fieldId?: Nullable<FieldId>,
): Promise<ProjectId> =>
  getClient('ProjectClient').projectCreateProject({
    companyUid: companyId,
    fieldUid: fieldId,
    projectName: name,
    projectType: type,
    projectYear,
  })

export const getProjectSizeApi = (projectId: ProjectId): Promise<number> =>
  getClient('ProjectClient').projectGetProjectSize(projectId)

/// upload

export const uploadMapApi = (projectId: ProjectId, files: UploadMapType[]): Promise<MapInfo[]> => {
  const client = getClient('LayersUploadClient')
  const fileCollection: File[] = []
  const mapProps: {
    DomainType: UploadMapType['domainType']
    GridName?: UploadMapType['name']
    NegativeValuesZ: UploadMapType['areZValuesNegative']
  }[] = []

  files.forEach(file => {
    fileCollection.push(file.file.data)
    mapProps.push({
      DomainType: file.domainType,
      GridName: file.name,
      NegativeValuesZ: file.areZValuesNegative,
    })
  })
  const mapPropsJson = JSON.stringify(mapProps)
  return client
    .layersUploadUploadMap2DFileCollection(projectId, {
      gridInfoCollection: mapPropsJson,
      fileCollection,
    })
    .then(header => header.map(convertToMapInfo))
}

export const getInterpretationsIdsFromHorizonApi = (
  projectId: ProjectId,
  horizonIds: HorizonId[],
): Promise<BoundingBoxParams> => {
  const client = getClient('HorizonClient')
  return client.horizonGetInterpretationIdsByHorizonId(projectId, horizonIds)
}

export const addUploadedCubeToProjectApi = (
  projectId: ProjectId,
  cubeId: CubeId | UploadId,
): Promise<void> => {
  const client = getClient('CubeClient')
  return client.cubeAddCubeToProject({ projectId, cubeId })
}

export const calculateHorizonBoundingBoxApi = (
  horizon2dIds: string[],
  horizon3dIds: string[],
): Promise<BoundingBoxGeometry> => {
  const client = getClient('HorizonClient')
  return client.horizonCalcBoundingBox({ horizon2dIds, horizon3dIds }).then(convertToGeometry)
}

/// authentication

export const logLoginApi = (): Promise<ApiUserInfo> => {
  const client = getClient('AuthenticationClient')
  return client.authenticationLogin()
}

export const logLogoutApi = (): void => {
  const client = getClient('AuthenticationClient')
  return void client.authenticationLogout()
}

// error logging
export const logStackTraceApi = (errorText: string, stackTrace: string): void => {
  const client = getClient('FrontendLoggingClient')
  client
    .frontendLoggingProcessException({ user: getUserDeviceInfo(), text: errorText, stackTrace })
    .catch(err => {
      console.debug('Не получилось отправить или обработать лог ошибки 😥', err)
    })
}

const sendLogsFromConsoleApi = (messages: LogMessage[]): void => {
  const client = getClient('FrontendLoggingClient')
  client
    .frontendLoggingProcessConsole({
      user: getUserDeviceInfo(),
      messages: convertLogMessages(messages),
    })
    .catch(err => {
      console.debug('Не получилось отправить или обработать лог вывода в консоль 😥', err)
    })
}

export const logFromConsoleApi = throttleCallback(sendLogsFromConsoleApi, 15_000)

const logHttpRequests = (requests: HttpRequestLog[]): void => {
  const client = getClient('FrontendLoggingClient')
  client
    .frontendLoggingProcessNetwork({
      user: getUserDeviceInfo(),
      messages: convertHttpRequestLogs(requests),
    })
    .catch(err => {
      console.debug('Не получилось отправить или обработать лог запроса 😥', err)
    })
}

export const logHttpRequestApi = throttleCallback(logHttpRequests, 15_000)

/// cubeLine
export const getCubeLineValueForTabApi = (cubeLine: CubeLineForTab): Promise<number> => {
  const client = getClient('ProjectCubeLineClient')
  const cubeLineForTab = convertToApiCubeLineValue(cubeLine)
  return client.projectCubeLineGetCubeLineValueForTab(cubeLineForTab).then(convertToCubeLineValue)
}

export const updateCubeLineValueForTabApi = (cubeLine: UpdateCubeLineForTab): Promise<void> => {
  const client = getClient('ProjectCubeLineClient')
  const cubeLineForTab = convertToApiUpdateCubeLineValue(cubeLine)
  return client.projectCubeLineUpdateCubeLineValueForTab(cubeLineForTab)
}

/// Vsp

export const getActiveVspOffsetForProjectWellsApi = (
  projectId: ProjectId,
  fieldId: FieldId,
): Promise<ProjectWellVsp[]> => {
  const client = getClient('GeologyClient')
  return client
    .geologyGetWellsWithVspInProject(projectId, fieldId)
    .then(projectVsp => convertWellsVsp(projectVsp, fieldId))
}
